import { CloudSyncOutlined, SaveOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Descriptions,
  Divider,
  Drawer,
  Popconfirm,
  Select,
  Space,
  Switch,
  Tabs,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { http } from '../../api/client.js';
import { tx } from '../../i18n/translate.js';
import { DingTalkOrgSyncHistoryPanel } from './DingTalkOrgSyncHistoryPanel.js';

interface DingTalkOrgSyncDrawerProps {
  open: boolean;
  onClose: () => void;
  onSynced: () => void;
}

interface DingTalkSchedule {
  enabled: boolean;
  intervalHours: number;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastResult: string | null;
  updatedAt: string;
}

interface SyncResponse {
  summary: {
    departments: { created: number; updated: number };
    users: { created: number; updated: number; disabled: number };
  };
}

const intervalOptions = [
  { value: 6, label: tx('legacy:pages.admin.butreepage.scheduleEvery6h') },
  { value: 12, label: tx('legacy:pages.admin.butreepage.scheduleEvery12h') },
  { value: 24, label: tx('legacy:pages.admin.butreepage.scheduleEvery24h') },
  { value: 168, label: tx('legacy:pages.admin.butreepage.scheduleEvery168h') },
];

function formatTime(value: string | null | undefined) {
  return value
    ? dayjs(value).format('YYYY-MM-DD HH:mm')
    : tx('legacy:pages.admin.butreepage.notScheduled');
}

export function DingTalkOrgSyncDrawer(props: DingTalkOrgSyncDrawerProps) {
  const { message } = App.useApp();
  const [schedule, setSchedule] = useState<DingTalkSchedule | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [intervalHours, setIntervalHours] = useState(24);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [historyRefreshToken, setHistoryRefreshToken] = useState(0);

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get<DingTalkSchedule>('/admin/org-sync/dingtalk/schedule');
      setSchedule(data);
      setEnabled(data.enabled);
      setIntervalHours(data.intervalHours);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (props.open) void loadSchedule();
  }, [loadSchedule, props.open]);

  const runNow = async () => {
    setSyncing(true);
    try {
      const { data } = await http.post<SyncResponse>('/admin/org-sync/dingtalk/run');
      message.success(
        tx('legacy:pages.admin.butreepage.dingtalkSyncDone', {
          deptCreated: data.summary.departments.created,
          deptUpdated: data.summary.departments.updated,
          userCreated: data.summary.users.created,
          userUpdated: data.summary.users.updated,
        }),
      );
      props.onSynced();
      setHistoryRefreshToken((value) => value + 1);
      await loadSchedule();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(
        err.response?.data?.message ??
          err.message ??
          tx('legacy:pages.admin.butreepage.dingtalkSyncFailed'),
      );
    } finally {
      setSyncing(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      const { data } = await http.put<DingTalkSchedule>('/admin/org-sync/dingtalk/schedule', {
        enabled,
        intervalHours,
      });
      setSchedule(data);
      message.success(tx('legacy:pages.admin.butreepage.scheduleSaved'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={tx('legacy:pages.admin.butreepage.dingtalkSync')}
      open={props.open}
      onClose={props.onClose}
      width={760}
      destroyOnClose
      loading={loading}
    >
      <Tabs
        items={[
          {
            key: 'settings',
            label: tx('legacy:pages.admin.butreepage.syncSettingsTab'),
            children: (
              <>
                <Alert
                  type="warning"
                  showIcon
                  message={tx('legacy:pages.admin.butreepage.syncWarningTitle')}
                  description={tx('legacy:pages.admin.butreepage.syncWarningDesc')}
                />

                <Divider orientation="left">{tx('legacy:pages.admin.butreepage.runNow')}</Divider>
                <Popconfirm
                  title={tx('legacy:pages.admin.butreepage.confirmSyncTitle')}
                  description={tx('legacy:pages.admin.butreepage.confirmSyncDesc')}
                  okText={tx('legacy:pages.admin.butreepage.runNow')}
                  cancelText={tx('hardcoded:pages.admin.butreepage.498dc9fe')}
                  onConfirm={() => void runNow()}
                >
                  <Button icon={<CloudSyncOutlined />} loading={syncing} type="primary">
                    {tx('legacy:pages.admin.butreepage.runNow')}
                  </Button>
                </Popconfirm>

                <Divider orientation="left">
                  {tx('legacy:pages.admin.butreepage.scheduleTitle')}
                </Divider>
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                  <Space>
                    <Switch checked={enabled} onChange={setEnabled} />
                    <span>{tx('legacy:pages.admin.butreepage.enableSchedule')}</span>
                  </Space>
                  <Select
                    value={intervalHours}
                    onChange={setIntervalHours}
                    options={intervalOptions}
                    style={{ width: '100%' }}
                  />
                  <Button
                    icon={<SaveOutlined />}
                    loading={saving}
                    onClick={() => void saveSchedule()}
                  >
                    {tx('legacy:pages.admin.butreepage.saveSchedule')}
                  </Button>
                  <Descriptions column={1} size="small" bordered>
                    <Descriptions.Item label={tx('legacy:pages.admin.butreepage.nextRun')}>
                      {formatTime(schedule?.nextRunAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label={tx('legacy:pages.admin.butreepage.lastRun')}>
                      {formatTime(schedule?.lastRunAt)}
                    </Descriptions.Item>
                    <Descriptions.Item label={tx('legacy:pages.admin.butreepage.lastResult')}>
                      {schedule?.lastResult ?? tx('legacy:pages.admin.butreepage.notScheduled')}
                    </Descriptions.Item>
                  </Descriptions>
                </Space>
              </>
            ),
          },
          {
            key: 'history',
            label: tx('legacy:pages.admin.butreepage.syncHistoryTab'),
            children: (
              <DingTalkOrgSyncHistoryPanel open={props.open} refreshToken={historyRefreshToken} />
            ),
          },
        ]}
      />
    </Drawer>
  );
}
